import { Box, Button, Grid, Modal, TextField, Typography, Snackbar, Tooltip } from '@mui/material'
import FormControl, { useFormControl } from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import { createSelector } from '@reduxjs/toolkit'
import axios from 'axios'
import { MuiOtpInput } from 'mui-one-time-password-input'
import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ErrorAlert from '../../Layouts/ErrorAlert'
import { AuthContext } from '../../libs/contexts/authContext'
import { filterNonDigits, isValidEmail } from '../../libs/utils'
import { apiURL, getAffiliateInfo, verifyEmailStepOne, verifyEmailStepTwo } from '../../store/actions/authActions'
import { PasswordCheck } from '../authUI/CreatePasswordPage'
import { selectMsgText, selectMsgTitle } from './utils'
import ClearIcon from '@mui/icons-material/Clear'
import ReusableSnackbar from '../../Layouts/ReusableSnackbar'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

const selectExistingUser = (state) => state.auth.existingUser
const selectVerificationAttempted = (state) => state.auth.verificationAttempted
const selectVerificationCodeGenerated = (state) => state.auth.verificationCodeGenerated
const selectEmailVerified = (state) => state.auth.emailVerified
const selectNecessaryProps = createSelector(
  [
    selectExistingUser,
    selectVerificationAttempted,
    selectVerificationCodeGenerated,
    selectMsgTitle,
    selectMsgText,
    selectEmailVerified,
  ],
  (existingUser, verificationAttempted, verificationCodeGenerated, msgTitle, msgText, emailVerified) => ({
    existingUser,
    verificationAttempted,
    verificationCodeGenerated,
    msgTitle,
    msgText,
    emailVerified,
  })
)

function FirstRegisterStep({
  registrationForm,
  setRegistrationForm,
  workAddress,
  setWorkAddress,
  handleRegistrationFormChange,
  handleNext,
}) {
  const dispatch = useDispatch()
  const { existingUser, verificationCodeGenerated, verificationAttempted, msgTitle, msgText, emailVerified } =
    useSelector(selectNecessaryProps)
  const { affiliateCode, invalidUrl, productId } = useContext(AuthContext)
  const [showError, setShowError] = useState(false)
  const [showVerificationError, setShowVerificationError] = useState(false)
  const [invalidEmail, setInvalidEmail] = useState(false)
  const [agtIdMsg, setAgtIdMsg] = useState('')
  const [invalidAgtId, setInvalidAgtId] = useState(false)
  const [resendDisabled, setResendDisabled] = useState(false)
  const [countdown, setCountdown] = useState(60)
  const { workEmail, password, agent_id } = registrationForm
  const [doPasswordsMatch, setDoPasswordsMatch] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [otp, setOtp] = useState('')
  const [messageToggle, setMessageToggle] = useState(false)

  useEffect(() => {
    const validateEmail = () => {
      if (workEmail.length >= 5) {
        setInvalidEmail(!isValidEmail(workEmail))
      } else {
        setInvalidEmail(false)
      }
    }

    validateEmail()
  }, [workEmail])

  useEffect(() => {
    const isEmailVerifiable = () => {
      if (emailVerified) {
        setShowError(false)
        setOpenModal(false)
        setShowVerificationError(false)
        return
      }

      if (existingUser) {
        setShowError(true)
      } else if (verificationAttempted && verificationCodeGenerated) {
        if (msgText === 'Verification code does not match.') {
          setShowVerificationError(true)
          return
        }
        setShowError(false)
        setOpenModal(true)
      }
    }
    isEmailVerifiable()
  }, [existingUser, verificationCodeGenerated, verificationAttempted, msgText, emailVerified, messageToggle])

  useEffect(() => {
    if (resendDisabled) {
      startResendTimer()
    }
    return () => {
      setResendDisabled(false)
      setCountdown(60)
    }
  }, [resendDisabled])

  const handleOtpChange = (val) => {
    const filteredVal = filterNonDigits(val)
    setOtp(filteredVal)
  }

  //here first verify the agent with agentId, if success verify email
  const verifyEmail = () => {
    if (verificationCodeGenerated || verificationAttempted) {
      setOpenModal(true)
      return
    }
    const inputPayload = {
      affiliateCode,
      email: workEmail,
      productId: productId === '81403' ? 81403 : 81401
    }
    dispatch(verifyEmailStepOne(inputPayload))
  }

  const checkAgentId = async () => {
    const res = await dispatch(
      getAffiliateInfo({
        agentId: agent_id,
        productId: productId === '81403' ? 81403 : 81401
      })
    )
    if (res.statusCode === 200) {
      setRegistrationForm((prev) => ({
        ...prev,
        firstName: res.firstName || prev.firstName,
        lastName: res.lastName || prev.lastName,
        workAgentPhone: res.workAgentPhone || prev.workAgentPhone,
      }))
      setWorkAddress((prev) => ({
        ...prev,
        city: res.workAgentCity || prev.city,
        state: res.workAgentState || prev.state,
        address1: res.workAgentStreet1 || prev.address1,
        address2: res.workAgentStreet2 || prev.address2,
        zipCode: res.workAgentZip || prev.zipCode,
      }))
      verifyEmail()
    } else {
      setInvalidAgtId(true)
      setAgtIdMsg(res.msgText)
    }
  }

  const startResendTimer = () => {
    setResendDisabled(true)
    let timeLeft = 60
    const timer = setInterval(() => {
      timeLeft--
      setCountdown(timeLeft)
      if (timeLeft === 0) {
        setResendDisabled(false)
        clearInterval(timer)
      }
    }, 1000)
  }

  const resend = async () => {
    const inputPayload = {
      affiliateCode,
      email: workEmail,
    }
    await axios.post(`${apiURL}`, {
      ...inputPayload,
      prsCode: '3702F702',
    })
    setResendDisabled(true)
    setCountdown(60)
  }

  const matchVerificationCode = () => {
    const inputPayload = {
      affiliateCode,
      email: workEmail,
      verification_code: Number(otp),
      productId: productId === '81403' ? 81403 : 81401
    }
    dispatch(verifyEmailStepTwo(inputPayload))
    setMessageToggle(!messageToggle)
  }

  const MyFormHelperText = () => {
    const { focused } = useFormControl() || {}

    const helperText = React.useMemo(() => {
      if (focused) {
        return 'This field is being focused'
      }

      return 'Please verify your email address before creating your password'
    }, [focused])

    return <FormHelperText>{helperText}</FormHelperText>
  }

  const handleClose = (event, reason) => {
    setShowVerificationError(false)
  }
  const action = (
    <Button onClick={handleClose} color="secondary" size="small">
      <ClearIcon />
    </Button>
  )

  return (
    <>
      {/* <Snackbar
        open={showVerificationError}
        autoHideDuration={10000}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '&.MuiSnackbar-anchorOriginTopRight': { top: '100px' },
        }}
        message={msgText}
        action={action}
      /> */}
      {(showVerificationError || showError) && (
        <ReusableSnackbar title={msgTitle} message={msgText} alertType="warning" />
      )}
      {/* <ErrorAlert showError={showError} setShowError={setShowError} msgTitle={msgTitle} messageText={msgText} /> */}
      <Box component="form" sx={{ '& .MuiTextField-root': { m: 1 } }} noValidate autoComplete="off">
        <Typography align={'left'} sx={registerStepStyle.subheading}>
          Login Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormControl fullWidth>
              <TextField
                id="outlined-disabled"
                label="SBLI Agent ID"
                type="number"
                name="agent_id"
                value={agent_id}
                onChange={(e) => {
                  setInvalidAgtId(false)
                  setAgtIdMsg('')
                  handleRegistrationFormChange(e)
                }}
                InputLabelProps={{ shrink: true }}
                sx={registerStepStyle.textField}
                required
                error={invalidAgtId}
                helperText={invalidAgtId ? agtIdMsg : ''}
                InputProps={{
                  inputProps: {
                    inputMode: 'numeric',
                  },
                  endAdornment: (
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        <Box>
                          <Typography variant="body2">Locate your SBLI Agent ID here:</Typography>
                          <Typography variant="caption">
                            {productId === '81401' ? `Initial contracting email from SBLI, www.SBLIAgent.com in the "Business Summary" section, or contact SBLI Broker Services at quility@sbli.com or (855) 642-0023` : 
                            `Initial contracting email from SBLI, www.SBLIAgent.com in the "Business Summary" section, or contact SBLI Broker Services at (855) 642-0023`
                            }
                          </Typography>
                        </Box>
                      }
                    >
                      <InfoOutlinedIcon />
                    </Tooltip>
                  ),
                }}
              />
            </FormControl>
          </Grid>
          <Grid item lg={10} md={10} sm={10} xs={12}>
            <FormControl fullWidth>
              <TextField
                id="outlined-disabled"
                label="Work Email Address (Login)"
                placeholder="ex. saral@afficiency.com"
                type="email"
                name="workEmail"
                value={workEmail}
                onChange={handleRegistrationFormChange}
                InputLabelProps={{ shrink: true }}
                sx={registerStepStyle.textField}
                required
                error={invalidEmail}
                helperText={invalidEmail ? 'Please enter a valid email address' : ''}
              />
              <MyFormHelperText />
            </FormControl>
          </Grid>
          <Grid item lg={2} md={2} sm={2} xs={12} justify="center" alignItems="center">
            <Button
              onClick={checkAgentId}
              sx={registerStepStyle.verifyButton}
              disabled={invalidEmail || workEmail.length < 5 || emailVerified || invalidUrl || agent_id.length < 3}
            >
              Verify
            </Button>
          </Grid>
          <Modal
            open={openModal}
            onClose={() => setOpenModal(false)}
            sx={{
              marginTop: '12rem',
            }}
          >
            <Box sx={registerStepStyle.blueBox}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { lg: '36px', md: '36px', sm: '36px', xs: '24px' },
                }}
              >
                Confirm your Identity
              </Typography>
              <Typography color="#36414E" fontSize={'16px'} align="center" padding="12px 0px 32px 0px">
                We sent a code to {workEmail}. Enter it here to confirm your identity.
              </Typography>
              <MuiOtpInput
                inputMode="numeric"
                value={otp}
                length={6}
                onChange={handleOtpChange}
                TextFieldsProps={{ variant: 'standard' }}
              ></MuiOtpInput>
              <Typography color="#36414E" fontSize={'16px'} align="center" padding="32px 0px">
                Didn't receive the code?
                <Button onClick={resend} disabled={resendDisabled}>
                  {resendDisabled ? `Resend in ${countdown}s` : 'Resend'}
                </Button>
              </Typography>
              <Button sx={registerStepStyle.nextButton} onClick={matchVerificationCode} disabled={otp.length < 6}>
                Verify
              </Button>
            </Box>
          </Modal>
          <PasswordCheck
            password={password}
            setPassword={(v) =>
              setRegistrationForm({
                ...registrationForm,
                password: v,
              })
            }
            doPasswordsMatch={doPasswordsMatch}
            setDoPasswordsMatch={setDoPasswordsMatch}
          />
        </Grid>
        <Button
          sx={registerStepStyle.nextButton}
          onClick={handleNext}
          disabled={!doPasswordsMatch || !emailVerified || invalidUrl}
        >
          Next
        </Button>
      </Box>
    </>
  )
}

export default FirstRegisterStep

const registerStepStyle = {
  verifyButton: {
    textTransform: 'none',
    width: { lg: '90%', md: '90%', sm: '90%', xs: '60%' },
    height: '40px',
    mt: { sm: '15px', xs: '0px' },
    backgroundColor: '#C4CAF1',
    color: '#3C4FB9',
    borderRadius: '100px',
    '&:hover': {
      backgroundColor: '#9BA5DF',
      color: '#36414E',
    },
    '&:disabled': {
      backgroundColor: '#C4CAF1',
      color: 'white',
      textTransform: 'none',
    },
  },

  nextButton: {
    textTransform: 'none',
    width: { lg: '50%', md: '50%', sm: '50%', xs: '60%' },
    height: '40px',
    backgroundColor: '#3C4FB9',
    color: 'white',
    borderRadius: '100px',
    mt: '10px',
    '&:hover': {
      backgroundColor: '#9BA5DF',
      color: '#36414E',
    },
    '&:disabled': {
      backgroundColor: '#C4CAF1',
      color: 'white',
      textTransform: 'none',
    },
  },

  textField: {
    backgroundColor: 'white',
    '& input[type=number]': {
      '-moz-appearance': 'textfield',
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
    },
  },

  subheading: {
    padding: '30px 0px 15px 0px',
  },

  blueBox: {
    display: 'flex',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    pt: { lg: '52px', md: '52px', sm: '39px', xs: '24px' },
    pr: { lg: '52px', md: '52px', sm: '39px', xs: '16px' },
    pb: { lg: '52px', md: '52px', sm: '39px', xs: '24px' },
    pl: { lg: '52px', md: '52px', sm: '39px', xs: '16px' },
    width: { lg: '502px', md: '502px', sm: '502px', xs: '343px' },
    height: { lg: '438px', md: '438px', sm: '438px', xs: '386px' },
    background: '#F5F7FF',
    borderRadius: '8px',
    top: '40%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}
